/**
 * Reach HUD feature (M3 plan R15, order `FEATURE_ORDER.reachHud`, reach mode only): after M1's HUD, so it projects
 * through the same shaken camera. Mounts `#reach-hud` under `ctx.hudRoot` (M1's HUD files are never touched; reach.css
 * only hides M1's wave readout in reach mode). Per frame: refill the camera and source in place, run the pure model
 * (src/hud/reach/model.ts), then the diffed view (view.ts). The viewport size is cached from resize events, never read
 * per frame. `frame` allocates nothing while the HUD is steady (D-014).
 *
 * The relay repair list's required quantities come from R19's relink subsystem when it exposes
 * `requiredParts(relayId, part)`; until then the list shows the delivered counts against "?".
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { loadGalaxyConfig } from '../../data/galaxyConfig'
import { defaultFactionsConfig, FACTION_KEYS } from '../../galaxy/layout'
import type { FactionKey } from '../../galaxy/layout/types'
import type { RegionId } from '../../galaxy/types'
import { createHudCamera, setHudCamera, type HudCamera } from '../../hud/projection'
import { loadReachHudConfig, type ReachHudConfig } from '../../hud/reach/config'
import { keyGlyph, type HudNames } from '../../hud/reach/labels'
import {
  createReachHudState,
  updatePadActive,
  updateReachHudModel,
  type ReachHudRules,
  type ReachHudSource,
  type ReachHudState,
  type RepairListSource,
} from '../../hud/reach/model'
import '../../hud/reach/reach.css'
import { createReachHudView, mountReachHud, renderReachHud, setReachHudFrame, setReachHudLabelMode, type ReachHudView } from '../../hud/reach/view'
import { loadReachControls, type ReachControls } from '../../reach/controls'
import type { ReachCore } from '../../reach/runtime/types'
import type { PartId } from '../../reach/types'
import { loadTravelConfig, type TravelConfig } from '../../travel/config'
import { Life, Target } from '../../world/components'

/** The pad glyph for the interact hold (plan §2.10: D-pad ↓ held 0.5 s). */
export const PAD_INTERACT_GLYPH = 'D-pad ↓ (hold)'

/** The pad glyph for the cruise chord (plan §2.10: L3 + R3 held). */
export const PAD_CRUISE_GLYPH = 'L3 + R3'

/** Display names from galaxy.json (regions) and factions.json (short names). */
export function hudNames(): HudNames {
  const regions = {} as Record<RegionId, string>
  for (const r of loadGalaxyConfig().regions) regions[r.id] = r.label
  const f = defaultFactionsConfig().factions
  const factions = {} as Record<FactionKey, string>
  for (const key of FACTION_KEYS) factions[key] = f[key]?.short ?? key
  return { regions, factions }
}

/** The HUD's fixed rules from the configs. */
export function createReachHudRules(hud: ReachHudConfig, travel: TravelConfig, controls: ReachControls, names: HudNames): ReachHudRules {
  return {
    hud,
    travel,
    scanNeedsNoTarget: controls.deepScanRequiresNoTarget,
    keyGlyph: keyGlyph(controls.keyboard.interact[0]),
    padGlyph: PAD_INTERACT_GLYPH,
    cruiseKeyHint: `HOLD ${keyGlyph(controls.keyboard.cruise[0])} TO CRUISE`,
    cruisePadHint: `HOLD ${PAD_CRUISE_GLYPH} TO CRUISE`,
    names,
  }
}

function hasRequiredParts(s: unknown): s is RepairListSource {
  return typeof s === 'object' && s !== null && typeof (s as Partial<RepairListSource>).requiredParts === 'function'
}

/** R19's relink subsystem when it exposes `requiredParts(relayId, part)`; null otherwise. */
export function findRepairSource(core: ReachCore): RepairListSource | null {
  for (let i = 0; i < core.subsystems.length; i++) {
    const s: unknown = core.subsystems[i]
    if (hasRequiredParts(s)) {
      return { requiredParts: (relayId: string, part: PartId): number => s.requiredParts(relayId, part) }
    }
  }
  return null
}

export interface ReachHudFeature extends GameFeature {
  /** Null outside reach mode and before init. */
  readonly state: ReachHudState | null
  readonly rules: ReachHudRules
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export interface ReachHudOptions {
  hud?: ReachHudConfig
  travel?: TravelConfig
  controls?: ReachControls
  names?: HudNames
  /** Overrides the repair-list source (tests); default: `findRepairSource(core)` at init. */
  repair?: RepairListSource | null
  /** Mounts the DOM (default true; tests pass a view factory instead). */
  mount?: (ctx: GameContext, rules: ReachHudRules) => { view: ReachHudView; host: HTMLElement | null }
  /** The viewport size (default: window.innerWidth/innerHeight, cached on resize). */
  viewport?: () => { width: number; height: number }
}

function defaultMount(ctx: GameContext, rules: ReachHudRules): { view: ReachHudView; host: HTMLElement | null } {
  const { host, el } = mountReachHud(ctx.hudRoot, rules)
  return { view: createReachHudView(el, rules.hud.labels), host }
}

export function createReachHudFeature(opts: ReachHudOptions = {}): ReachHudFeature {
  const rules = createReachHudRules(
    opts.hud ?? loadReachHudConfig(),
    opts.travel ?? loadTravelConfig(),
    opts.controls ?? loadReachControls(),
    opts.names ?? hudNames(),
  )
  const cam: HudCamera = createHudCamera()
  let state: ReachHudState | null = null
  let view: ReachHudView | null = null
  let host: HTMLElement | null = null
  let src: ReachHudSource | null = null
  let width = 1
  let height = 1
  let padActive = false
  const readViewport = (): void => {
    const size = opts.viewport?.() ?? { width: window.innerWidth, height: window.innerHeight }
    width = size.width
    height = size.height
  }
  let listening = false
  let offSettings: (() => void) | null = null

  return {
    name: 'reachHud',
    order: FEATURE_ORDER.reachHud,
    rules,
    get state(): ReachHudState | null {
      return state
    },

    init(ctx: GameContext): void {
      const core = ctx.reach
      if (core === null) return
      const mounted = (opts.mount ?? defaultMount)(ctx, rules)
      view = mounted.view
      host = mounted.host
      // The HUD labels setting (spec 2026-10-09 §5.6, card F2): now and on every change.
      const labelView = mounted.view
      setReachHudLabelMode(labelView, ctx.settings.hudLabels, rules)
      offSettings = ctx.bus.on('settings', (s) => setReachHudLabelMode(labelView, s.hudLabels, rules))
      state = createReachHudState(core, rules.hud)
      // Both cursors start at 0: the system banner and toasts from the boot's initial entry show once flying.
      src = {
        core,
        playerEid: ctx.sim.playerEid,
        alpha: 1,
        dt: 0,
        paused: true,
        cam,
        padActive: false,
        repair: opts.repair !== undefined ? opts.repair : findRepairSource(core),
      }
      readViewport()
      if (opts.viewport === undefined) {
        window.addEventListener('resize', readViewport)
        listening = true
      }
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      if (state === null || view === null || src === null) return
      const camera = ctx.camera
      setHudCamera(cam, ctx.rig.pos, camera.position, camera.quaternion, camera.fov, camera.zoom, width, height)
      padActive = updatePadActive(padActive, ctx.input, rules.hud.device.padAxisThreshold)
      src.playerEid = ctx.sim.playerEid
      src.alpha = f.alpha
      src.dt = f.dt
      src.paused = f.paused
      src.padActive = padActive
      updateReachHudModel(state, src, rules)
      // H2: labels near the reticle hide while a combat target is locked (the same test as M1's target bracket).
      const p = ctx.sim.playerEid
      const e = p >= 0 ? Target.eid[p] : -1
      setReachHudFrame(view, width, height, e >= 0 && e !== p && Life.alive[e] === 1)
      renderReachHud(view, state, rules)
    },

    dispose(): void {
      if (listening) window.removeEventListener('resize', readViewport)
      listening = false
      offSettings?.()
      offSettings = null
      host?.remove()
      host = null
      view = null
      state = null
      src = null
    },
  }
}

const feature: ReachHudFeature = createReachHudFeature()

export default feature
